import axios from 'axios';
import {useQuery} from "react-query"
import { useAppState } from '../context/zustand';

export const myAxios = axios.create({
    timeout: 8000,
    // backend IP
    baseURL: (window.env && window.env.API_URL) || 'http://localhost:8000'
});

const responseMessages = {
    expiredCredentials: "Credentials expired! Please log in again!",
    backendError: "Unexpected error! Please contact our support@buttonsystems.eu",
    invalidCredentials: "No active account found with the given credentials!",
    refreshUnavailable: "Could not refresh session. Please try again."
}

function isRequestUrl(config, url) {
    return config?.url === url || config?.url?.endsWith(url);
}

function handleExpiredSession(getState) {
    getState().logout();
    getState().setAlert({severity: "error", message: responseMessages.expiredCredentials});
}

function handleRetryableRefreshFailure(getState) {
    getState().setAlert({severity: "error", message: responseMessages.refreshUnavailable});
}

// Add a request interceptor
myAxios.interceptors.request.use(
    (config) => {
        let token = localStorage.getItem('access');
        if (token) {
            config.headers['Authorization'] = 'Bearer ' + (token || "");
        }
        config.headers['Content-Type'] = (config.headers['Content-Type'] || 'application/json');
        return config
    },
    error => {
        console.log('Request error')
        return Promise.reject(error)
    });

//Add a response interceptor
myAxios.interceptors.response.use(
    (response) => {
        return Promise.resolve(response);
    }, async (error) => {
        const originalRequest = error.config;
        console.log(`Error: ${error}`)
        const {getState} = useAppState

        if (!originalRequest) {
            getState().setAlert({severity:"error", message:responseMessages.backendError})
            return Promise.reject("Request error");
        }

        if (!error.response) {
            console.log('Error: Network Error');
            getState().setAlert({severity:"error", message:responseMessages.backendError})
            return Promise.reject('Network Error');
        }

        if (error.response.status === 400 && isRequestUrl(originalRequest, '/api/token/')) {
            return Promise.reject(error.response);
        }

        if (error.response.status === 401 && isRequestUrl(originalRequest, '/api/token/refresh/')) {
            handleExpiredSession(getState);
            return Promise.reject("Invalid refresh");
        }

        if (
            error.response.status === 406 &&
            (
                isRequestUrl(originalRequest, '/api/upload/') ||
                isRequestUrl(originalRequest, '/api/upload/rss-check/')
            )
        ) {
            return Promise.reject(error.response);
        }

        if (error.response.status === 401 && !originalRequest._retry) {
            console.log("Refreshing token")
            originalRequest._retry = true;
            const refreshToken = localStorage.getItem('refresh')
            if(!refreshToken){
                handleExpiredSession(getState);
                return Promise.reject("Invalid credentials")
            }

            let refreshResponse;
            try {
                refreshResponse = await axios.post(
                    `${originalRequest.baseURL || myAxios.defaults.baseURL}/api/token/refresh/`,
                    { "refresh": refreshToken },
                    { timeout: myAxios.defaults.timeout }
                );
            }
            catch (err) {
                const refreshStatus = err?.response?.status;

                if (refreshStatus >= 400 && refreshStatus < 500) {
                    console.log("Refresh token problem");
                    handleExpiredSession(getState);
                    return Promise.reject("Refresh token problem")
                }

                handleRetryableRefreshFailure(getState);
                return Promise.reject(err?.response || err || "Refresh failed");
            }

            if (refreshResponse.status !== 200 || !refreshResponse.data?.access) {
                handleExpiredSession(getState);
                return Promise.reject("Invalid refresh response");
            }

            console.log("Token refreshed!")
            localStorage.setItem('access', refreshResponse.data.access);
            const conf = {
                ...originalRequest,
                headers: {
                    ...(originalRequest.headers || {}),
                    'Authorization': "Bearer " + refreshResponse.data.access
                }
            }
            return myAxios(conf);
        }

        getState().setAlert({severity:"error", message:responseMessages.backendError})
        return Promise.reject("Request error");
    });

export function useAxios(
    key,
    axiosParams,
    reactQueryProps = {},
    onSuccess = (res) => res,
    onError = (err) => err,
    onDone = () => {}
) {
    const props = useQuery(
        key,
        () =>
        myAxios(axiosParams)
                .then((res) => {
                    onSuccess(res)
                    onDone()
                    return Promise.resolve(res)
                })
                .catch((err) => {
                    onError(err)
                    onDone()
                    return Promise.reject(err)
                }),

        {
            enabled: false,
            retry: false,
            cacheTime: 2*1000,
            staleTime: 2*1000,
            ...reactQueryProps,
        }
    )

    return props
}
    
